import React from "react";
import PropTypes from "prop-types";
import { Field, Fields } from "redux-form";
import { Col, FormGroup } from "react-bootstrap";
import { get, isEmpty, isPlainObject } from "lodash";
import {
  MonthYearField,
  Select,
  NumberInput,
  FormInput,
  Check,
  CheckGroup
} from "MODULES/shared/components/Forms";
import CurrencyField from "MODULES/shared/components/Forms/CurrencyField";
import {
  OUTGOING_LINE_ITEMS,
  PRICE_PER_UNIT_OPTIONAL_QUOTE_TYPES,
  EXCHANGE_ONLY,
  MAKE_OFFER,
  NO_QUOTE,
  AS_IS,
  OFFER,
  PRICE_PER_UNIT,
  EXCHANGE_FEE,
  WARRANTY,
  FOLLOW_UP_FEEDBACK,
  FOLLOW_UP_FEEDBACKS,
  HAS_FOLLOW_UP_FEEDBACK,
  OUTRIGHT_VALUE,
  INCLUDE_DOM
} from "MODULES/OutgoingQuotes/constants";
import {
  OVERHAULED,
  REPAIRED,
  INSPECTED,
  NEW_ITEM,
  TRACE_TYPE,
  TRACED_TO,
  TAG_DATE,
  TAGGED_BY,
  SERIAL_NUMBER,
  CONDITION,
  REPAIR_AND_HOLD,
  LEAD_TIME,
  WARRANTY_OPTIONS,
  FAA,
  EASA,
  CAAC,
  UK_CAA,
  TCCA,
  OTHER,
  OTHER_TAG_TYPE
} from "MODULES/inventories/constants";
import { arrayToOptions, toOptions } from "LIB/forms";
import {
  CONDITIONS,
  CONDITIONS_WITH_SERVICEABLE,
  INVENTORY_ID,
  MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION,
  QUANTITY
} from "APP_ROOT/constants";
import { properCase } from "LIB/formatters/text";
import {
  setLeadTime,
  setTagDate,
  setTraceTo,
  setTraceType,
  warrantyForCondition as _warrantyForCondition
} from "MODULES/shared/helpers";
import OfferFields from "MODULES/OutgoingQuotes/shared/OfferFields";
import { BEYOND_ECON_REPAIR } from "MODULES/Receiving/constants";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import TagTypes from "MODULES/inventories/components/EditInventory/TagTypes";
import { DOM, TOOLING_PART_TYPE } from "MODULES/parts/constants";
import { useSettings } from "APP_ROOT/hooks/useCurrentUser";
import moment from "moment";
import { dollars } from "LIB/formatters/currency";
import NoteTooltip from "../../shared/components/NoteTooltip/NoteTooltip";

export function warrantyForCondition(
  condition,
  part,
  tagDate,
  inventory,
  quoteType
) {
  if (part && part.pass_fail) {
    return "30 days";
  }

  const defaultWarranty = _warrantyForCondition(
    condition,
    part,
    tagDate,
    inventory
  );

  const months = useSettings().tag_date_months_for_warranty || null;
  if (
    months &&
    tagDate &&
    moment(tagDate).isBefore(moment().subtract(Number(months), "month"))
  ) {
    return useSettings().tag_date_warranty;
  }

  if (quoteType === AS_IS) {
    return "No Warranty, unit sold as-is.";
  }

  const settings = useSettings();

  if (settings && settings[`${condition}_warranty`]) {
    return settings[`${condition}_warranty`];
  }

  if (inventory) {
    if ([REPAIRED, INSPECTED, OVERHAULED].includes(inventory.condition)) {
      if (inventory.warranty_override) {
        return inventory.warranty_override;
      } else if (part.warranty_override) {
        return part.warranty_override;
      }
    } else if (
      inventory.condition === NEW_ITEM &&
      inventory.warranty_override
    ) {
      return inventory.warranty_override;
    } else if (inventory.condition === NEW_ITEM) {
      return defaultWarranty;
    }
  }

  return defaultWarranty;
}

export const copyFromInventoryFields = {
  caac: inv => inv.caac,
  condition: inv => inv.srp_condition,
  dom: (inv, _quoteType, part) => (part.dom_sensitive ? inv.dom : null),
  easa: inv => inv.easa,
  faa: inv => inv.faa,
  uk_caa: inv => inv.uk_caa,
  lead_time: setLeadTime,
  price_per_unit: (inv, quoteType) =>
    inv.condition === BEYOND_ECON_REPAIR || quoteType === MAKE_OFFER
      ? null
      : inv.suggested_retail,
  exchange_fee: (inv, quoteType, part, canExchange) => {
    if (
      (part && part.pass_fail) ||
      inv.condition === BEYOND_ECON_REPAIR ||
      !canExchange ||
      quoteType === MAKE_OFFER
    ) {
      return "";
    }
    return inv.exchange_srp;
  },
  other: inv => !isEmpty(inv.other_tag_type),
  other_tag_type: inv => inv.other_tag_type,
  quantity: inv => inv.quantity || 0,
  serial_number: inv => inv.serial_number,
  tag_date: setTagDate,
  tagged_by: inv => inv.tagged_by,
  tcca: inv => inv.tcca,
  trace_type: setTraceType,
  traced_to: setTraceTo,
  warranty: (inv, quoteType, part) =>
    warrantyForCondition(inv.srp_condition, part, inv.tag_date, inv, quoteType)
};

export const copyFromInventoryFieldsOnOQ = {
  ...copyFromInventoryFields,
  condition: inv => {
    if (inv.condition === BEYOND_ECON_REPAIR) {
      return inv.condition;
    }

    return inv.srp_condition;
  },
  tagged_by: inv => {
    if (inv.status !== REPAIR_AND_HOLD) {
      return inv.tagged_by;
    }

    return get(inv, "most_recent_repair_order.company.name");
  },
  total_cycles_remaining: inv => {
    const totalCycles = get(inv, "part.total_cycles");
    if (totalCycles && inv.total_cycles) {
      return Number(totalCycles) - Number(inv.total_cycles);
    }

    return null;
  },
  total_hours_remaining: inv => {
    const totalHours = get(inv, "part.total_hours");
    if (totalHours && inv.total_cycles) {
      return Number(totalHours) - Number(inv.total_hours);
    }

    return null;
  }
};

const hasDisabledFields = disabledFields => {
  if (!isPlainObject(disabledFields)) {
    return false;
  }

  return fieldName => Boolean(disabledFields[fieldName]);
};

export default function PartDetails({
  canExchange,
  disableTagType,
  formName,
  formSection,
  getFieldValue,
  resetFields,
  setFieldValue,
  quoteType,
  handleQuantityChanged,
  part,
  selectedInventories,
  doNotExchangeInventoryLineItem,
  setWarrantyField,
  inventoryIds = [],
  passFail,
  children,
  disabled
}) {
  const isDisabled = hasDisabledFields(disabled);
  const doNotExchange =
    selectedInventories &&
    selectedInventories.length > 0 &&
    selectedInventories.filter(f => f).every(t => t.do_not_exchange);

  const sectionName = name => [formSection, name].filter(f => f).join(".");
  const SELECTABLE_CONDITIONS = get(part, TOOLING_PART_TYPE)
    ? CONDITIONS_WITH_SERVICEABLE
    : CONDITIONS;

  const minOutrightVal = useSettings()[
    MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION
  ];

  const renderExchangeFeeNoteTooltip = () => {
    const reasons = [];
    if (doNotExchangeInventoryLineItem) {
      reasons.push("<li>due to manual restriction set to inv. record</li>");
    }
    if (passFail) {
      reasons.push("<li>due to pass/fail nature</li>");
    }
    if (doNotExchange) {
      reasons.push("<li>due to manual restriction set to inv. record</li>");
    }
    if (Number(getFieldValue(PRICE_PER_UNIT)) < Number(minOutrightVal)) {
      reasons.push(`<li>Price Per Unit under ${dollars(minOutrightVal)}</li>`);
    }

    if (reasons.length === 0) return null;

    return (
      <NoteTooltip
        placement="right"
        icon="fa-lg fa-info-circle"
        color="rgba(255, 77, 77, 0.5)"
        note={
          `Exchange Fee is disabled due to the following reasons:` +
          `<br/>` +
          `<ul>${reasons.join("")}</ul>`
        }
      />
    );
  };

  return (
    <div>
      <FormRow>
        <Col md={4}>
          <FormGroup>
            <Field
              name={CONDITION}
              component={Select}
              options={toOptions(SELECTABLE_CONDITIONS)}
              onChange={e =>
                setWarrantyField(
                  warrantyForCondition(
                    e.target.value,
                    part,
                    null,
                    null,
                    quoteType
                  )
                )}
              label={properCase(CONDITION)}
              disabled={inventoryIds.length > 1}
              required={inventoryIds.length <= 1}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup>
            <Field
              name={QUANTITY}
              label="QTY"
              onChange={() => handleQuantityChanged()}
              component={NumberInput}
              roundDigits={0}
              inputProps={{ min: 0 }}
              disabled={inventoryIds.length > 1}
              required={inventoryIds.length <= 1}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <CurrencyField
              name={PRICE_PER_UNIT}
              label={
                quoteType === EXCHANGE_ONLY
                  ? properCase(OUTRIGHT_VALUE)
                  : properCase(PRICE_PER_UNIT)
              }
              onBlur={(_, val) => {
                if (minOutrightVal && Number(minOutrightVal) > Number(val)) {
                  setFieldValue(sectionName(EXCHANGE_FEE))(null);
                }
              }}
              required={
                !PRICE_PER_UNIT_OPTIONAL_QUOTE_TYPES.includes(quoteType)
              }
              readOnly={quoteType === MAKE_OFFER}
              disabled={quoteType === MAKE_OFFER || isDisabled(PRICE_PER_UNIT)}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <CurrencyField
              name={EXCHANGE_FEE}
              label={["Exch Fee", renderExchangeFeeNoteTooltip()]}
              readOnly={quoteType === NO_QUOTE}
              disabled={
                doNotExchangeInventoryLineItem ||
                passFail ||
                [MAKE_OFFER, NO_QUOTE].includes(quoteType) ||
                isDisabled(EXCHANGE_FEE) ||
                !canExchange ||
                (part && part.pass_fail) ||
                doNotExchange ||
                (minOutrightVal &&
                  Number(minOutrightVal) > getFieldValue(PRICE_PER_UNIT))
              }
              required={quoteType === EXCHANGE_ONLY}
            />
          </FormGroup>
        </Col>
      </FormRow>
      {quoteType === OFFER && <OfferFields resetFields={resetFields} />}
      {children}
      <hr className="hr-xs hr-dashed" style={{ marginBottom: 15 }} />
      <FormRow>
        <Col md={6}>
          <FormGroup>
            <Field
              name={TRACE_TYPE}
              component={Select}
              options={toOptions(OUTGOING_LINE_ITEMS.traceTypes)}
              disabled={inventoryIds.length > 1}
              label={properCase(TRACE_TYPE)}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Field
              name={TRACED_TO}
              component={FormInput}
              label={properCase(TRACED_TO)}
              disabled={inventoryIds.length > 1}
            />
          </FormGroup>
        </Col>
      </FormRow>
      <FormRow>
        <Col md={4}>
          <FormGroup>
            <MonthYearField
              name={TAG_DATE}
              component={FormInput}
              label={properCase(TAG_DATE)}
              disabled={inventoryIds.length > 1}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Field
              name={TAGGED_BY}
              component={FormInput}
              label={properCase(TAGGED_BY)}
              disabled={inventoryIds.length > 1}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <MonthYearField
              name={DOM}
              component={FormInput}
              label={DOM.toUpperCase()}
              disabled={inventoryIds.length > 0 || getFieldValue(INVENTORY_ID)}
            />
            {(inventoryIds.length === 1 || getFieldValue(INVENTORY_ID)) &&
              part &&
              selectedInventories[0] &&
              selectedInventories[0].dom &&
              !part.dom_sensitive && [
                <input
                  type="checkbox"
                  style={{ marginLeft: "10px" }}
                  checked={getFieldValue(INCLUDE_DOM)}
                  onClick={() => {
                    const includeDom = getFieldValue(INCLUDE_DOM);
                    if (!includeDom) {
                      setFieldValue(sectionName(DOM))(
                        selectedInventories[0][DOM]
                      );
                    } else {
                      setFieldValue(sectionName(DOM))(null);
                    }
                    setFieldValue(sectionName(INCLUDE_DOM))(!includeDom);
                  }}
                />,
                " Include DOM"
              ]}
          </FormGroup>
        </Col>
      </FormRow>
      <FormRow>
        <Col md={4}>
          <FormGroup>
            <Field
              name={SERIAL_NUMBER}
              component={FormInput}
              label="Serial #"
              disabled={inventoryIds.length > 1}
            />
          </FormGroup>
        </Col>
        <Col md={5}>
          <FormGroup>
            <Field
              component={Select}
              label={properCase(WARRANTY)}
              name={WARRANTY}
              options={arrayToOptions(WARRANTY_OPTIONS)}
              disabled={inventoryIds.length > 1}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <Field
              name={LEAD_TIME}
              component={NumberInput}
              label={[
                "Days Lead Time",
                <NoteTooltip
                  color="rgba(255, 255, 255, 0.25)"
                  icon="fa-info-circle"
                  note="0 = In Stock"
                />
              ]}
              roundDigits={0}
            />
          </FormGroup>
        </Col>
      </FormRow>
      <FormRow>
        <Col md={12}>
          <Fields
            disabled={disableTagType}
            names={[FAA, EASA, CAAC, UK_CAA, TCCA, OTHER, OTHER_TAG_TYPE]}
            component={TagTypes}
            formName={formName}
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col md={4}>
          <CheckGroup checkGroupStyles={{ marginTop: 0 }} justified>
            <Field
              name={HAS_FOLLOW_UP_FEEDBACK}
              component={Check}
              label={properCase(FOLLOW_UP_FEEDBACK)}
              onChange={(_, val) => {
                if (!val) setFieldValue(FOLLOW_UP_FEEDBACK)(null);
              }}
            />
          </CheckGroup>
        </Col>
        <Col md={6}>
          {getFieldValue(HAS_FOLLOW_UP_FEEDBACK) && (
            <FormGroup>
              <Field
                label={properCase(FOLLOW_UP_FEEDBACK)}
                name={FOLLOW_UP_FEEDBACK}
                component={Select}
                options={toOptions(FOLLOW_UP_FEEDBACKS)}
              />
            </FormGroup>
          )}
        </Col>
      </FormRow>
      <FormGroup>
        <Field name={"ships_from"} component={FormInput} label={"fob"} />
      </FormGroup>
    </div>
  );
}

PartDetails.propTypes = {
  canExchange: PropTypes.bool,
  disableTagType: PropTypes.bool,
  formName: PropTypes.string,
  formSection: PropTypes.string,
  getFieldValue: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  resetFields: PropTypes.func.isRequired,
  inventoryIds: PropTypes.arrayOf(PropTypes.number),
  part: PropTypes.shape({
    pass_fail: PropTypes.bool
  }),
  quoteType: PropTypes.string,
  handleQuantityChanged: PropTypes.func.isRequired,
  selectedInventories: PropTypes.arrayOf(PropTypes.shape({})),
  setWarrantyField: PropTypes.func.isRequired,
  passFail: PropTypes.bool,
  children: PropTypes.element,
  disabled: PropTypes.shape({
    price_per_unit: PropTypes.bool,
    exchange_fee: PropTypes.bool
  })
};

PartDetails.defaultProps = {
  disabled: {
    price_per_unit: false,
    exchange_fee: false
  }
};



// WEBPACK FOOTER //
// ./app/modules/OutgoingQuotes/shared/PartDetails.jsx